import { useState } from "react";
import { CheckCircle2, CircleDashed, Download, Loader2, Play, XCircle, MinusCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtTime, BACKEND } from "@/lib/api";
import { usePoll, PageHeader, Panel, Pill, Empty } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const STEPS = [
  ["warmup", "Warm-up", "Channel on air on source 1"],
  ["soak", "Soak", "No gaps, no sequence resets, no stray discontinuities"],
  ["kill_source", "Kill source", "Source 1 killed → source 2 with one discontinuity"],
  ["ac3", "AC-3", "AC-3 backup converted to AAC at the output"],
  ["failback", "Failback", "Back to source 1 only after the failback window"],
  ["restart", "Restart", "Channel restart continues the sequence"],
  ["all_down", "All down", "Held frame/slate keeps flowing, off air → back on air"],
];
const ICON = { passed: [CheckCircle2, "text-emerald-400"], failed: [XCircle, "text-red-400"], running: [Loader2, "text-cyan-400 animate-spin"], pending: [CircleDashed, "text-slate-500"], skipped: [MinusCircle, "text-slate-600"] };
const TONE = { passed: "green", failed: "red", running: "cyan", error: "red", interrupted: "amber" };

function StepRow({ s }) {
  const [open, setOpen] = useState(false);
  const [I, c] = ICON[s.status] || ICON.pending;
  const meta = STEPS.find((x) => x[0] === s.name) || [];
  const ev = s.evidence || {};
  return (
    <div data-testid={`step-${s.name}`} className="border-b border-slate-800/60">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-3 py-2 text-left hover:bg-slate-800/30 px-2 rounded">
        <I size={16} className={c} />
        <span className="w-28 text-sm text-slate-200">{meta[1]}</span>
        <span className="flex-1 text-xs text-slate-500">{meta[2]}</span>
        <span className="font-mono text-xs text-slate-400">{s.duration_s != null ? `${s.duration_s}s` : ""}</span>
        <Pill tone={TONE[s.status] || "slate"} testid={`step-status-${s.name}`}>{s.status}</Pill>
      </button>
      {open && s.evidence && (
        <div className="px-9 pb-3 space-y-2">
          <div className="grid md:grid-cols-2 gap-x-6 gap-y-0.5 font-mono text-[11px]">
            {Object.entries(ev).filter(([k]) => !["playlist", "events", "before", "after", "state"].includes(k)).map(([k, v]) => (
              <div key={k} className="flex gap-2"><span className="text-slate-500">{k}</span><span className="text-slate-200">{String(v)}</span></div>
            ))}
          </div>
          {ev.events?.length > 0 && <div className="font-mono text-[11px] text-slate-400 space-y-0.5">{ev.events.map((e, i) => <div key={i}>{typeof e === "string" ? e : `${e.kind}: ${e.message || ""}`}</div>)}</div>}
        </div>
      )}
    </div>
  );
}

function RunView({ id }) {
  const { data: r } = usePoll(`/admin/acceptance/runs/${id}`, 2000);
  if (!r) return null;
  return (
    <Panel title={`Run ${r.id}`} testid="acceptance-run" right={<div className="flex items-center gap-2">
      <Pill tone={TONE[r.status]} testid="run-status">{r.status}</Pill>
      <a data-testid="report-md-btn" href={`${BACKEND}/api/admin/acceptance/runs/${r.id}/report?fmt=md`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-cyan-300 hover:bg-slate-800"><Download size={12} /> Report (.md)</a>
      <a data-testid="report-json-btn" href={`${BACKEND}/api/admin/acceptance/runs/${r.id}/report?fmt=json`} className="inline-flex items-center gap-1 px-2 py-1 rounded text-xs text-slate-400 hover:text-cyan-300 hover:bg-slate-800"><Download size={12} /> Evidence (.json)</a>
    </div>}>
      <div className="font-mono text-[11px] text-slate-500 mb-2">started {fmtTime(r.started_at, true)} · soak {r.options.soak_seconds}s · failback {r.options.failback_window}s{r.finished_at && ` · finished ${fmtTime(r.finished_at, true)}`}{r.error && <span className="text-red-400"> · {r.error}</span>}</div>
      {r.steps.map((s) => <StepRow key={s.name} s={s} />)}
    </Panel>
  );
}

export default function Acceptance() {
  const { data: runs, reload } = usePoll("/admin/acceptance/runs", 4000);
  const [soak, setSoak] = useState(60);
  const [fb, setFb] = useState(30);
  const [steps, setSteps] = useState(STEPS.map((s) => s[0]));
  const [sel, setSel] = useState(null);
  const running = (runs || []).some((r) => r.status === "running");
  const start = async () => {
    try {
      const { data } = await api.post("/admin/acceptance/run", { soak_seconds: Number(soak), failback_window: Number(fb), steps });
      setSel(data.id); toast.success("Acceptance run started"); reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const shown = sel || runs?.[0]?.id;
  return (
    <div>
      <PageHeader title="Acceptance tests" sub="One click runs the spec v4 suite on a throw-away channel with two synthetic sources, then exports the evidence."
        right={<Button data-testid="run-acceptance-btn" disabled={running} onClick={start} className="bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold"><Play size={14} /> {running ? "Run in progress…" : "Run suite"}</Button>} />
      <div className="grid xl:grid-cols-[320px_1fr] gap-4">
        <div className="space-y-4">
          <Panel title="Options">
            <div className="grid grid-cols-2 gap-2 mb-3">
              <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Soak (s)</span><Input data-testid="soak-seconds-input" type="number" min={10} value={soak} onChange={(e) => setSoak(e.target.value)} className="h-8 bg-[#0B111B] border-slate-700 font-mono" /></label>
              <label className="space-y-1"><span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">Failback (s)</span><Input data-testid="failback-seconds-input" type="number" min={10} value={fb} onChange={(e) => setFb(e.target.value)} className="h-8 bg-[#0B111B] border-slate-700 font-mono" /></label>
            </div>
            {STEPS.map(([k, l]) => (
              <label key={k} className="flex items-center gap-2 text-sm text-slate-300 py-0.5"><Checkbox data-testid={`step-toggle-${k}`} checked={steps.includes(k)} disabled={k === "warmup"} onCheckedChange={() => setSteps(steps.includes(k) ? steps.filter((x) => x !== k) : [...steps, k])} />{l}</label>
            ))}
            <p className="text-[11px] text-slate-500 mt-2">Full suite takes about 4–5 min plus soak time. Your own channels are not touched. A scheduled run fires nightly at 3:00 America/New_York and its result is sent through Alerts ("Acceptance report") — configure in Settings.</p>
          </Panel>
          <Panel title="History">
            {(runs || []).map((r) => (
              <button key={r.id} data-testid={`run-${r.id}`} onClick={() => setSel(r.id)} className={`w-full flex items-center justify-between py-1.5 px-2 rounded text-xs ${shown === r.id ? "bg-slate-800/70" : "hover:bg-slate-800/30"}`}>
                <span className="font-mono text-slate-400">{fmtTime(r.started_at, true)}</span>
                {r.trigger === "scheduled" && <Pill tone="blue">sched</Pill>}
                <span className="font-mono text-slate-500">{r.steps.filter((s) => s.status === "passed").length}/{r.steps.filter((s) => s.status !== "skipped").length}</span>
                <Pill tone={TONE[r.status]}>{r.status}</Pill>
              </button>
            ))}
            {runs && runs.length === 0 && <p className="text-xs text-slate-500">No runs yet.</p>}
          </Panel>
        </div>
        {shown ? <RunView id={shown} /> : <Empty>Run the suite to produce your first evidence report.</Empty>}
      </div>
    </div>
  );
}
